import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Grid,
	Typography,
	FormControlLabel,
	Switch,
	Divider,
	TextField,
	Chip,
	Link,
	Checkbox,
	IconButton,
	Box,
} from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { Alert } from "@material-ui/lab";
import { FormSubmitButton, Feedback } from "frame/components";
import AlertEmailForm from "./alert-email-form";
import { useRouter } from "utils";
import * as fromRoutes from "../../actions/types";
import {
	featureFlagsFetch,
	featureFlagsSave,
	onSaveLeaveHours,
	onSaveEmailNotifications,
} from "../../actions";

const useStyles = makeStyles((theme) => ({
	root: {
		margin: theme.spacing(3, 0, 2, 0),
		maxWidth: 800,
		"& > *": {
			paddingLeft: "0 !important",
			paddingRight: "0 !important",
		},
	},
	section: {
		marginBottom: theme.spacing(3),
	},
	sectionHeading: {
		fontWeight: theme.typography.fontWeightBold,
	},
	switchLabel: {
		marginBottom: theme.spacing(1),
		display: "flex",
		width: "100%",
	},
	description: {
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
		fontSize: "0.875rem",
	},
	divider: {
		margin: theme.spacing(2, 0),
	},
	saveMessage: {
		color: theme.palette.success.main,
		margin: theme.spacing(1, 2),
		position: "absolute",
	},
	warning: {
		marginBottom: theme.spacing(2),
	},
	paidBadge: {
		marginLeft: theme.spacing(1),
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		fontSize: "0.75rem",
		height: 20,
	},
	paidLink: {
		cursor: "pointer",
		color: theme.palette.primary.main,
		textDecoration: "underline",
		"&:hover": {
			textDecoration: "none",
		},
	},
	disabledSwitch: {
		opacity: 0.6,
	},
	subOption: {
		marginLeft: theme.spacing(3),
		paddingLeft: theme.spacing(2),
		borderLeft: `2px solid ${theme.palette.divider}`,
	},
	csvOrderRow: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(0.5, 0),
		"& .csvOrderLabel": {
			flex: 1,
		},
	},
}));

const FeatureFlagsForm = ({
	featureFlags,
	loading,
	saving,
	onFetch,
	onSave,
	leaveHoursPerDay,
	onSaveLeaveHours,
	allowExternalNotifications,
	onSaveEmailNotifications,
	incidentAlertEmails,
	incidentAlertEmailsLoading,
	onSaveIncidentAlertEmails,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const [flags, setFlags] = useState({
		incidents: true,
		inductionMeetings: true,
		safetyMinutes: true,
		staffTraining: false,
		siteInspections: true,
		jsa: true,
		siteSafetyPlans: true,
		workTypes: true,
		equipment: false,
		equipmentUsageRecording: false,
		leave: true,
		tasks: false,
		manualTimesheetEntries: false,
		timesheetTimeEditHistory: false,
		timesheetApprovals: false,
		// Optional list of column keys to include in the default Timesheet CSV export.
		// When null/empty, all columns are exported.
		timesheetCsvColumns: null,
		// Terminology overrides — empty string means use the built-in default.
		customerLabel: "",
		siteLabel: "",
	});
	const initialLeaveHours = Number.isFinite(leaveHoursPerDay) ? leaveHoursPerDay : 8;
	const [leaveHours, setLeaveHours] = useState(initialLeaveHours);
	const [showSaved, setShowSaved] = useState(false);
	const [warnings, setWarnings] = useState([]);
	const [restrictExternalRecipients, setRestrictExternalRecipients] = useState(
		!allowExternalNotifications
	);
	const [showEmailSaved, setShowEmailSaved] = useState(false);

	useEffect(() => {
		onFetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Only sync Redux → local state when a fetch just finished (loading: true → false).
	// Reacting to every `featureFlags` object reference change would reset user edits
	// mid-keystroke whenever a background fetch completes.
	const prevLoadingRef = useRef(loading);
	useEffect(() => {
		const wasLoading = prevLoadingRef.current;
		prevLoadingRef.current = loading;

		// Sync only on the transition from loading → loaded (fetch just completed).
		if (wasLoading && !loading && featureFlags) {
			setFlags({
				incidents: featureFlags.incidents ?? true,
				inductionMeetings: featureFlags.inductionMeetings ?? true,
				safetyMinutes: featureFlags.safetyMinutes ?? true,
				staffTraining: featureFlags.staffTraining ?? false,
				siteInspections: featureFlags.siteInspections ?? true,
				jsa: featureFlags.jsa ?? true,
				siteSafetyPlans: featureFlags.siteSafetyPlans ?? true,
				workTypes: featureFlags.workTypes ?? true,
				equipment: featureFlags.equipment ?? false,
				equipmentUsageRecording:
					(featureFlags.equipment ?? false) &&
					(featureFlags.equipmentUsageRecording ?? false),
				leave: featureFlags.leave ?? true,
				tasks: featureFlags.tasks ?? false,
				manualTimesheetEntries: featureFlags.manualTimesheetEntries ?? false,
				timesheetTimeEditHistory: featureFlags.timesheetTimeEditHistory ?? false,
				timesheetApprovals: featureFlags.timesheetApprovals ?? false,
				timesheetCsvColumns: featureFlags.timesheetCsvColumns ?? null,
				customerLabel: featureFlags.customerLabel ?? "",
				siteLabel: featureFlags.siteLabel ?? "",
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	useEffect(() => {
		const next = Number.isFinite(leaveHoursPerDay) ? leaveHoursPerDay : 8;
		setLeaveHours(next);
	}, [leaveHoursPerDay]);

	useEffect(() => {
		setRestrictExternalRecipients(!allowExternalNotifications);
	}, [allowExternalNotifications]);

	const onEmailNotificationToggle = (event) => {
		const checked = event.target.checked;
		setRestrictExternalRecipients(checked);
		// Persist immediately (separate from Feature Flags save).
		onSaveEmailNotifications(!checked);
		setShowEmailSaved(true);
		setTimeout(() => setShowEmailSaved(false), 3000);
	};

	const handleToggle = (flagName) => (event) => {
		const newValue = event.target.checked;
		const updatedFlags = {
			...flags,
			[flagName]: newValue,
		};

		// Check for dependency warnings
		const newWarnings = [];
		
		// If disabling Incidents, check if dependencies exist
		if (flagName === "incidents" && !newValue) {
			if (updatedFlags.inductionMeetings || updatedFlags.jsa || updatedFlags.siteSafetyPlans) {
				newWarnings.push(
					"Disabling Incidents will also disable Hazard Library access. However, Induction Meetings, JSA, and Site Safety Plans require Hazard Library. Consider enabling Incidents or disabling those features first."
				);
			}
		}

		// If disabling a feature that requires Hazard Library
		if (
			(flagName === "inductionMeetings" || flagName === "jsa" || flagName === "siteSafetyPlans") &&
			!newValue &&
			!updatedFlags.incidents
		) {
			// This is fine - they're disabling a dependent feature
		}

		// If enabling a dependent feature but Incidents is disabled
		if (
			(flagName === "inductionMeetings" || flagName === "jsa" || flagName === "siteSafetyPlans") &&
			newValue &&
			!updatedFlags.incidents
		) {
			// Auto-enable Incidents (which includes Hazard Library)
			updatedFlags.incidents = true;
			newWarnings.push(
				`Enabling ${flagName === "inductionMeetings" ? "Induction Meetings" : flagName === "jsa" ? "JSA" : "Site Safety Plans"} requires Hazard Library, so Incidents has been automatically enabled.`
			);
		}

		setFlags(updatedFlags);
		setWarnings(newWarnings);
	};

	const allTimesheetCsvColumns = [
		{ key: "Staff", label: "Staff Member (Name)" },
		{ key: "FullName", label: "Full Name (First + Last)" },
		{ key: "StartDate", label: "Start Date" },
		{ key: "StartTime", label: "Start Time" },
		{ key: "LunchTime", label: "Breaks" },
		{ key: "EndDate", label: "End Date" },
		{ key: "EndTime", label: "End Time" },
		{ key: "NetHours", label: "Hours (excluding breaks)" },
		{ key: "Notes", label: "Notes (e.g. Allowance)" },
		{ key: "SiteOrLeaveType", label: "Site / Leave Type" },
		{ key: "CreatedAt", label: "Created At" },
		{ key: "Organisation", label: "Organisation" },
		{ key: "StartGeolocation", label: "Start Geolocation" },
		{ key: "EndGeolocation", label: "End Geolocation" },
		{ key: "StaffType", label: "Staff Type" },
		{ key: "TaskType", label: "Task Type" },
		{ key: "Equipment", label: "Equipment" },
	];

	const selectedCsvColumns =
		flags.timesheetCsvColumns && flags.timesheetCsvColumns.length
			? flags.timesheetCsvColumns
			: allTimesheetCsvColumns.map((c) => c.key);

	const labelByKey = Object.fromEntries(
		allTimesheetCsvColumns.map((c) => [c.key, c.label])
	);

	// Add column to end of list (order = tick order)
	const addTimesheetCsvColumn = (columnKey) => () => {
		if (selectedCsvColumns.includes(columnKey)) return;
		const next = [...selectedCsvColumns, columnKey];
		setFlags((prev) => ({ ...prev, timesheetCsvColumns: next }));
	};

	// Remove column from list
	const removeTimesheetCsvColumn = (columnKey) => () => {
		const next = selectedCsvColumns.filter((k) => k !== columnKey);
		setFlags((prev) => ({
			...prev,
			timesheetCsvColumns: next.length ? next : null,
		}));
	};

	const moveCsvColumn = (index, direction) => () => {
		const newIndex = index + direction;
		if (newIndex < 0 || newIndex >= selectedCsvColumns.length) return;
		const next = [...selectedCsvColumns];
		[next[index], next[newIndex]] = [next[newIndex], next[index]];
		setFlags((prev) => ({ ...prev, timesheetCsvColumns: next }));
	};

	const resetTimesheetCsvColumns = () => {
		setFlags((prev) => ({
			...prev,
			timesheetCsvColumns: null,
		}));
	};

	const availableCsvColumns = allTimesheetCsvColumns.filter(
		(c) => !selectedCsvColumns.includes(c.key)
	);

	const handleSave = () => {
		// Keep Staff Training off while UI is hidden (STAFF_TRAINING_UI_ENABLED)
		onSave({
			...flags,
			staffTraining: false,
			equipmentUsageRecording: flags.equipment && flags.equipmentUsageRecording,
		});
		const value = Number(leaveHours);
		const safeValue = isNaN(value) || value <= 0 ? 8 : value;
		onSaveLeaveHours(safeValue);
		setShowSaved(true);
		setTimeout(() => setShowSaved(false), 3000);
		setWarnings([]);
	};

	return (
		<Grid className={classes.root} container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h4">Feature Settings</Typography>
				<Typography variant="body1" className={classes.description}>
					Enable or disable features for your organization. Disabled features will be hidden from menus and their data will not be accessible.
				</Typography>
			</Grid>

			{warnings.length > 0 && (
				<Grid item xs={12}>
					{warnings.map((warning, index) => (
						<Alert key={index} severity="warning" className={classes.warning}>
							{warning}
						</Alert>
					))}
				</Grid>
			)}

			<Grid item xs={12} className={classes.section}>
				<Typography variant="h5" className={classes.sectionHeading}>
					MENU ITEMS
				</Typography>
				<Divider className={classes.divider} />

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.incidents}
							onChange={handleToggle("incidents")}
							color="primary"
						/>
					}
					label="Incidents"
				/>
				<Typography variant="body2" className={classes.description}>
					Includes Hazard Library and Hazardous Substance Library
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.inductionMeetings}
							onChange={handleToggle("inductionMeetings")}
							color="primary"
						/>
					}
					label="Induction Meetings"
				/>
				<Typography variant="body2" className={classes.description}>
					Requires Hazard Library (auto-enabled with Incidents)
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.safetyMinutes}
							onChange={handleToggle("safetyMinutes")}
							color="primary"
						/>
					}
					label="Safety Minutes"
				/>

				{/* Staff Training toggle hidden — feature unused (STAFF_TRAINING_UI_ENABLED) */}

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.leave}
							onChange={handleToggle("leave")}
							color="primary"
						/>
					}
					label="Leave"
				/>

				<Typography variant="body2" className={classes.description}>
					Basic recording of leave taken or booked.
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.tasks}
							onChange={handleToggle("tasks")}
							color="primary"
						/>
					}
					label="Tasks"
				/>
				<Typography variant="body2" className={classes.description}>
					Manage tasks for staff. Shows Tasks in the Activity menu for admins and My Tasks for staff.
				</Typography>

				{flags.leave && (
					<div style={{ marginTop: 8, marginBottom: 16 }}>
						<TextField
							id="leave_hours_per_day"
							type="number"
							label="Leave hours per full working day"
							variant="outlined"
							inputProps={{ min: 1, max: 24, step: 0.1 }}
							helperText="Defaults to 8 if not set"
							value={leaveHours}
							onChange={(e) => setLeaveHours(e.target.value)}
							style={{ width: '50%' }}
						/>
					</div>
				)}

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.siteInspections}
							onChange={handleToggle("siteInspections")}
							color="primary"
						/>
					}
					label="Site Inspections"
				/>
				<Typography variant="body2" className={classes.description}>
					Includes Site Inspection Checklists
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.jsa}
							onChange={handleToggle("jsa")}
							color="primary"
						/>
					}
					label="Job Safety Analysis (JSA)"
				/>
				<Typography variant="body2" className={classes.description}>
					Requires Hazard Library (auto-enabled with Incidents)
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.siteSafetyPlans}
							onChange={handleToggle("siteSafetyPlans")}
							color="primary"
						/>
					}
					label="Site Safety Plan (Checklist)"
				/>
				<Typography variant="body2" className={classes.description}>
					Requires Hazard Library (auto-enabled with Incidents)
				</Typography>
			</Grid>

			<Grid item xs={12} className={classes.section}>
				<Typography variant="h5" className={classes.sectionHeading}>
					ADDITIONAL FEATURES
				</Typography>
				<Divider className={classes.divider} />

				

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.equipment}
							onChange={(e) => {
								e.preventDefault();
								history.push(fromRoutes.ACCOUNT_SUBSCRIPTION_PATH);
							}}
							color="primary"
							disabled
							className={classes.disabledSwitch}
						/>
					}
					label={
						<span>
							Equipment
							<Chip
								label="Paid Add-on"
								size="small"
								className={classes.paidBadge}
							/>
						</span>
					}
				/>
				<Typography variant="body2" className={classes.description}>
					Adds an Equipment field to timesheets and management forms, allowing staff to record which plant, vehicles, or assets were used.
				</Typography>
				<Typography variant="body2" className={classes.description}>
					<Link
						className={classes.paidLink}
						onClick={() => history.push(fromRoutes.ACCOUNT_SUBSCRIPTION_PATH)}
					>
						Manage on Subscription page
					</Link>
					{" "}($2.00 per user/month)
				</Typography>

				{flags.equipment && (
					<div className={classes.subOption}>
						<FormControlLabel
							className={classes.switchLabel}
							control={
								<Switch
									checked={flags.equipmentUsageRecording}
									onChange={handleToggle("equipmentUsageRecording")}
									color="primary"
								/>
							}
							label="Equipment Usage Recording"
						/>
						<Typography variant="body2" className={classes.description}>
							When enabled, staff can record equipment usage from the Equipment page and use the
							&quot;Capture equipment usage&quot; option on timesheets and tasks.
						</Typography>
					</div>
				)}
			</Grid>

			<Grid item xs={12} className={classes.section}>
				<Typography variant="h5" className={classes.sectionHeading}>
					TIMESHEET SETTINGS
				</Typography>
				<Divider className={classes.divider} />


				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.workTypes}
							onChange={handleToggle("workTypes")}
							color="primary"
						/>
					}
					label="Work Types"
				/>
				<Typography variant="body2" className={classes.description}>
					Adds a Work Type field to timesheets and management forms, allowing staff to categorize the type of work being performed.
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.manualTimesheetEntries}
							onChange={handleToggle("manualTimesheetEntries")}
							color="primary"
						/>
					}
					label="Manual Timesheet Entries"
				/>
				<Typography variant="body2" className={classes.description}>
					When enabled, staff members can create and edit their own manual timesheets by selecting dates and times, similar to the admin timesheet form. Staff can also clone and edit their own timesheets. Staff can start a timesheet by choosing a start date and time, which then becomes an active timesheet.
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.timesheetTimeEditHistory}
							onChange={handleToggle("timesheetTimeEditHistory")}
							color="primary"
						/>
					}
					label="Timesheet Time Edit History"
				/>
				<Typography variant="body2" className={classes.description}>
					When enabled, timesheets show an indicator and a short changelog if start, end, or lunch was changed after the original capture. Edit history is always recorded; this setting only controls whether it is shown.
				</Typography>

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Switch
							checked={flags.timesheetApprovals}
							onChange={handleToggle("timesheetApprovals")}
							color="primary"
						/>
					}
					label="Timesheet Approvals"
				/>
				<Typography variant="body2" className={classes.description}>
					When enabled, direct supervisors and account administrators can approve or unapprove closed work timesheets. Approval is a status only and does not lock edits or deletes.
				</Typography>

				<Divider className={classes.divider} />

				<Typography variant="h6" className={classes.sectionHeading}>
					Timesheet CSV export
				</Typography>
				<Typography variant="body2" className={classes.description}>
					Choose which fields are included and the order they appear in the CSV. Add columns below; use the arrows to reorder.
				</Typography>

				<Typography variant="subtitle2" className={classes.sectionHeading} style={{ marginTop: 8 }}>
					Column order in CSV
				</Typography>
				<Box marginBottom={1}>
					{selectedCsvColumns.map((key, index) => (
						<div key={key} className={classes.csvOrderRow}>
							<span className="csvOrderLabel">
								{index + 1}. {labelByKey[key] || key}
							</span>
							<IconButton
								size="small"
								aria-label="Move up"
								disabled={index === 0}
								onClick={moveCsvColumn(index, -1)}
							>
								<ArrowUpwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								aria-label="Move down"
								disabled={index === selectedCsvColumns.length - 1}
								onClick={moveCsvColumn(index, 1)}
							>
								<ArrowDownwardIcon fontSize="small" />
							</IconButton>
							<Link
								component="button"
								variant="body2"
								className={classes.paidLink}
								onClick={removeTimesheetCsvColumn(key)}
								style={{ marginLeft: 4 }}
							>
								Remove
							</Link>
						</div>
					))}
				</Box>

				{availableCsvColumns.length > 0 && (
					<>
						<Typography variant="subtitle2" className={classes.sectionHeading}>
							Add column
						</Typography>
						<Grid container spacing={1}>
							{availableCsvColumns.map((col) => (
								<Grid item xs={12} sm={6} key={col.key}>
									<Link
										component="button"
										variant="body2"
										className={classes.paidLink}
										onClick={addTimesheetCsvColumn(col.key)}
									>
										+ {col.label}
									</Link>
								</Grid>
							))}
						</Grid>
					</>
				)}

				<Typography variant="body2" className={classes.description} style={{ marginTop: 8 }}>
					<Link
						className={classes.paidLink}
						onClick={resetTimesheetCsvColumns}
					>
						Reset to all fields (default order)
					</Link>
				</Typography>
			</Grid>

		<Grid item xs={12} className={classes.section}>
			<Typography variant="h5" className={classes.sectionHeading}>
				TERMINOLOGY
			</Typography>
			<Divider className={classes.divider} />
			<Typography variant="body2" className={classes.description}>
				Override the default "Customer" and "Site" labels used throughout the app. Leave blank to keep the defaults.
			</Typography>

			<Grid container spacing={2} style={{ marginBottom: 8 }}>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						variant="outlined"
						label='Customer label'
						placeholder='e.g. Client, Account, Boat'
						helperText='Defaults to "Customer" when left blank'
						value={flags.customerLabel}
						onChange={(e) => { const v = e.target.value; setFlags((prev) => ({ ...prev, customerLabel: v })); }}
						inputProps={{ maxLength: 50 }}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						variant="outlined"
						label='Site label'
						placeholder='e.g. Job, Location, Vessel'
						helperText='Defaults to "Site" when left blank'
						value={flags.siteLabel}
						onChange={(e) => { const v = e.target.value; setFlags((prev) => ({ ...prev, siteLabel: v })); }}
						inputProps={{ maxLength: 50 }}
					/>
				</Grid>
			</Grid>
		</Grid>

		<Grid item xs={12} className={classes.section}>
			<Typography variant="h5" className={classes.sectionHeading}>
				EMAIL NOTIFICATIONS
			</Typography>
			<Divider className={classes.divider} />

				<FormControlLabel
					className={classes.switchLabel}
					control={
						<Checkbox
							checked={restrictExternalRecipients}
							onChange={onEmailNotificationToggle}
							color="primary"
						/>
					}
					label="Only send emails to the users of ForTheRecord"
				/>
				<Typography variant="body2" className={classes.description}>
					Forms can only email Staff user emails (users of ForTheRecord). When enabled, external recipient sections are hidden/blocked.
				</Typography>
				{showEmailSaved && (
					<Feedback disableTimeout active color="success">
						Changes saved
					</Feedback>
				)}

				<Divider className={classes.divider} />

				<Typography variant="h6" className={classes.sectionHeading}>
					Incident alert emails
				</Typography>
				<Typography variant="body2" className={classes.description}>
					An email for all new accidents/incidents will be sent to the following email addresses.
				</Typography>

				<AlertEmailForm
					embedded
					emails={incidentAlertEmails || []}
					loading={incidentAlertEmailsLoading}
					onSave={onSaveIncidentAlertEmails}
				/>
			</Grid>

			<Grid item xs={12}>
				<FormSubmitButton onClick={handleSave} loading={saving}>
					Save Changes
				</FormSubmitButton>
				{showSaved && (
					<Feedback disableTimeout active color="success">
						Changes saved
					</Feedback>
				)}
			</Grid>
		</Grid>
	);
};

FeatureFlagsForm.propTypes = {
	featureFlags: PropTypes.object,
	loading: PropTypes.bool,
	saving: PropTypes.bool,
	onFetch: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	leaveHoursPerDay: PropTypes.number,
	onSaveLeaveHours: PropTypes.func.isRequired,
	allowExternalNotifications: PropTypes.bool.isRequired,
	onSaveEmailNotifications: PropTypes.func.isRequired,
	incidentAlertEmails: PropTypes.array,
	incidentAlertEmailsLoading: PropTypes.bool,
	onSaveIncidentAlertEmails: PropTypes.func,
};

const mapStateToProps = ({ featureFlags, profile }) => ({
	featureFlags: featureFlags,
	loading: featureFlags?.fetch?.loading || false,
	saving: featureFlags?.save?.saving || false,
	leaveHoursPerDay: Number.isFinite(profile?.leaveHoursPerDay)
		? profile.leaveHoursPerDay
		: undefined,
	allowExternalNotifications: Boolean(profile && profile.allowExternalNotifications),
});

const mapDispatchToProps = {
	onFetch: featureFlagsFetch,
	onSave: featureFlagsSave,
	onSaveLeaveHours,
	onSaveEmailNotifications,
};

export default connect(mapStateToProps, mapDispatchToProps)(FeatureFlagsForm);

